import React, { useState, useEffect, useRef } from "react";
import { Card, CardTitle, Bar, Badge, KpiCard, SentimentDot } from "../UI";
import { ANALYSIS_DONE_STATUSES, BRANDS_COLORS, FUNNEL_STAGES, font } from "./constants";
import { getAiSimulationDetails } from "../../requests";
import styles from "../../_css/Simulation.module.css";
import { toCapitalize } from "../helper/formatters";
import Empty from "./Empty";
import NextButton from "./NextButton";
import {
  pickLatestAiSimulationRun,
  describePlatformModel,
} from "./simulationRunHelpers";
import { isRTL } from "../../utils/helper";
import { isSimulationLanguageEnglish } from "./simulationTranslationStorage";

import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useRole from "../../hooks/getRole";
import { BarChart2Icon } from "lucide-react";

const SHOW_TRANSLATE_BUTTON = true;

const safeArray = (v) => (Array.isArray(v) ? v : []);
const safeObject = (v) =>
  v && typeof v === "object" && !Array.isArray(v) ? v : {};
const safeString = (v) => (typeof v === "string" ? v.trim() : "");
const safeNum = (v, def = 0) =>
  typeof v === "number" && !Number.isNaN(v)
    ? v
    : typeof v === "string"
      ? parseFloat(v) || def
      : def;
const safeKeys = (obj) =>
  obj && typeof obj === "object" && !Array.isArray(obj) ? Object.keys(obj) : [];
const na = (v, fallback = "Not available") =>
  v != null && v !== "" && (typeof v !== "string" || v.trim() !== "")
    ? v
    : fallback;

const PLATFORM_OPTIONS = [
  { id: "chatgpt", label: "ChatGPT", color: "#10a37f" },
  { id: "claude", label: "Claude", color: "#d97706" },
  { id: "gemini", label: "Gemini", color: "#4285f4" },
  { id: "perplexity", label: "Perplexity", color: "#1a1a2e" },
];

function ResponseTab({
  recentSimulationId = null,
  activeFunnel,
  recentSimulationDetails = null,
  recentSimulationStatus = null,
  selectedRun = null,
  onReturnToLatestRun = () => {},
  translateLanguage = null,
  translationViewLang = "or",
  onGoToCompetitive = null,
}) {
  const context = useSelector((state) => state.context);
  const userInfo = useSelector((state) => state.user);
  const role = useRole();
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [viewingTranslatedQueries, setViewingTranslatedQueries] = useState(
    translationViewLang === "en",
  );
  const [activePlatform, setActivePlatform] = useState("chatgpt");
  const location = useLocation();
  const [key, setKey] = useState(false);
  const navigate = useNavigate();
  const translationViewLangRef = useRef(translationViewLang);
  useEffect(() => {
    const translationParam = new URLSearchParams(location.search).get(
      "translate",
    );
    const isTranslation = translationParam === "true";

    if (isTranslation) {
      setViewingTranslatedQueries(true);
      translationViewLangRef.current = "en";
      return;
    }

    setViewingTranslatedQueries(translationViewLang === "en");
  }, [translationViewLang, location.search]);

  useEffect(() => {
    const translationParam = new URLSearchParams(location.search).get(
      "translate",
    );
    const isTranslation = translationParam === "true";

    // A specific historical run was explicitly picked in the Runs tab — show it regardless of
    // the simulation's live status, instead of falling through to the "always latest" fetch below.
    if (selectedRun) {
      setDetails({ ...(recentSimulationDetails ?? {}), status: 6 });
      setLoading(false);
      return;
    }

    if (
      recentSimulationDetails &&
      (recentSimulationStatus === 6 || recentSimulationStatus === 91)
    ) {
      setDetails(recentSimulationDetails);
      setLoading(false);
      return;
    }

    if (!recentSimulationId) {
      setDetails(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const showTranslation = isTranslation ? true : translationViewLang === "en";

    // setViewingTranslatedQueries((p)=> showTranslation ? true : false)
    getAiSimulationDetails(recentSimulationId, showTranslation)
      .then((res) => {
        setDetails(res?.data ?? null);
      })
      .catch(() => setDetails(null))
      .finally(() => setLoading(false));
  }, [
    recentSimulationId,
    recentSimulationDetails,
    recentSimulationStatus,
    translationViewLang,
    selectedRun,
  ]);

  const handleTranslateLanguage = async () => {
    if (!translateLanguage || translating) return;
    setTranslating(true);
    try {
      const lang = viewingTranslatedQueries ? "or" : "en";
      translationViewLangRef.current = lang;
      const response = await translateLanguage(lang);

      if (response) {
        if (role === "super_admin") {
          if (lang === "en") {
            navigate(
              `/simulations/details/${recentSimulationId}/response?simulation_id=${recentSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}&agency_id=${userInfo?.agencyId}&translate=true`,
            );
          } else {
            navigate(
              `/simulations/details/${recentSimulationId}/response?simulation_id=${recentSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}&agency_id=${userInfo?.agencyId}&translate=false`,
            );
          }
        } else {
          if (lang === "en") {
            navigate(
              `/simulations/details/${recentSimulationId}/response?simulation_id=${recentSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}&translate=true`,
            );
          } else {
            navigate(
              `/simulations/details/${recentSimulationId}/response?simulation_id=${recentSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}&translate=false`,
            );
          }
        }
      }
    } finally {
      setTranslating(false);
    }
  };

  if (!recentSimulationId) {
    return (
      <Empty message="Failed simulation. Please create a new simulation again" />
    );
  }

  if (details?.status == -1) {
    return (
      <Empty message="Failed simulation. Please create a new simulation again" />
    );
  }

  if (details?.status == 1 || details?.status == 2 || details?.status == 3) {
    return (
      <Empty message="Queries generation in progress..." type="progress" />
    );
  }

  if (details?.status == 4) {
    return (
      <Empty
        message="Queries generation completed. Please go to Queries tab and select the queries you want to run. Then click on the Run Simulation button to start the simulation."
        type="progress"
      />
    );
  }

  // A new run may be processing (status 5/90) or in some other transient state, but if an
  // earlier run already completed, keep showing its data instead of blocking the whole tab.
  const hasCompletedRun = safeArray(details?.aiSimulationRuns).length > 0;

  if ((details?.status == 5 || details?.status == 90) && !hasCompletedRun) {
    return (
      <Empty message="Response generation in progress..." type="progress" />
    );
  }

  if (!ANALYSIS_DONE_STATUSES.includes(Number(details?.status)) && !hasCompletedRun) {
    return (
      <Empty
        message="AI is still analyzing the data. Please wait for a moment."
        type="progress"
      />
    );
  }

  const run =
    pickLatestAiSimulationRun(details?.aiSimulationRuns) ??
    details?.aiSimulationRuns?.[0] ??
    null;

  // Per-platform breakdown (ai_simulation_run_models): one row per LLM platform for this run.
  // Falls back to the run's own (legacy, chatgpt-only) columns when no per-platform row exists yet.
  const platformModels = safeArray(run?.ai_simulation_run_models);
  const activePlatformModel =
    platformModels.find((m) => m?.platform === activePlatform) ?? null;
  const dataSource =
    activePlatformModel ?? (activePlatform === "chatgpt" ? run : null);
  const hasPlatformData = dataSource != null;
  // Which model answered on each platform, read from the saved run
  // (same data rule as dataSource above). null when the platform has no data in this run.
  const modelInfoFor = (platformId) => {
    const row = platformModels.find((m) => m?.platform === platformId) ?? null;
    // The run itself only stands in for ChatGPT on legacy runs without per-platform rows
    const hasData =
      row != null || (platformId === "chatgpt" && run != null && platformModels.length === 0);
    return hasData ? describePlatformModel(row, platformId) : null;
  };
  const activeModelInfo = modelInfoFor(activePlatform);

  const insights = safeObject(dataSource?.extracted_insights);
  const summaryMetrics = safeObject(
    dataSource?.summary_metrics ?? insights?.summary_metrics,
  );
  // F-07: Perception Summary moved here from Competitive Insights.
  const strategicAnalysis = safeObject(insights?.strategic_analysis);
  const perceptionSummary = safeString(strategicAnalysis?.perception_summary);
  // Visibility Verdict moved here from Competitive Insights.
  const visibilityVerdict = safeString(strategicAnalysis?.visibility_verdict);

  const runQueries = safeArray(run?.runQueries).filter(
    (q) => q?.platform === activePlatform,
  );
  const totalQueriesExecuted =
    dataSource?.total_queries_executed ??
    runQueries.length ??
    safeNum(summaryMetrics?.total_queries, 0);
  const totalQueriesWithMention =
    dataSource?.total_queries_with_mention ??
    safeNum(summaryMetrics?.mentioned_in, 0);
  const total = Math.max(0, totalQueriesExecuted);
  const mentionRate =
    dataSource?.client_mention_rate ??
    (total > 0
      ? (totalQueriesWithMention / total) * 100
      : summaryMetrics?.visibility_rate);
  const sov = dataSource?.client_share_of_voice ?? null;
  const avgPos =
    dataSource?.client_avg_position ?? summaryMetrics?.average_mention_position;
  const sentimentDist =
    dataSource?.sentiment_distribution ?? insights?.sentiment_distribution;
  const topObjections = safeArray(
    dataSource?.top_objections ?? insights?.strategic_analysis?.top_objections,
  );
  const strategicRecommendations = safeArray(
    dataSource?.strategic_recommendations ??
      insights?.strategic_analysis?.strategic_recommendations,
  );

  // Share of voice: use extracted_insights.share_of_voice (brand, percentage, queries_mentioned)
  const shareOfVoiceArr = safeArray(insights?.share_of_voice);
  const competitiveData = (() => {
    if (shareOfVoiceArr.length > 0) {
      return shareOfVoiceArr.reduce((acc, e) => {
        const n = e?.brand ?? e?.name ?? e;
        if (n != null && String(n).trim())
          acc[String(n).trim()] = safeNum(
            e?.percentage ?? e?.queries_mentioned ?? e?.mentions,
            0,
          );
        return acc;
      }, {});
    }
    const raw = dataSource?.competitive_landscape_data?.most_frequent_competitors;
    const comp = Array.isArray(raw)
      ? raw
      : safeArray(insights?.competitive_landscape?.most_frequent_competitors);
    if (comp.length > 0) {
      return comp.reduce((acc, c) => {
        const key =
          c != null
            ? typeof c === "string"
              ? c
              : safeString(c?.brand ?? c?.name ?? c) || String(c)
            : null;
        if (key)
          acc[key] = safeNum(c?.shared_mentions ?? c?.queries_mentioned, 1);
        return acc;
      }, {});
    }
    return null;
  })();

  const hl = (text) => {
    if (text == null || typeof text !== "string")
      return [{ text: "", hl: false }];
    const brands = safeKeys(BRANDS_COLORS);
    let parts = [{ text: String(text), hl: false }];
    brands.forEach((brand) => {
      const np = [];
      parts.forEach((p) => {
        if (p.hl) {
          np.push(p);
          return;
        }
        const re = new RegExp(
          `(${brand.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
          "gi",
        );
        const sp = p.text.split(re);
        sp.forEach((s) => {
          if (s.toLowerCase() === brand.toLowerCase())
            np.push({ text: s, hl: true, brand });
          else if (s) np.push({ text: s, hl: false });
        });
      });
      parts = np;
    });
    return parts;
  };

  // Parse response into blocks (headings + paragraphs), strip # and * symbols
  const formatResponseBlocks = (text) => {
    if (!text || typeof text !== "string") return [];
    const stripMarkdown = (s) => s.replace(/\*+/g, "").trim();
    const lines = text.split(/\r?\n/);
    const blocks = [];
    let paragraphBuffer = [];

    const flushParagraph = () => {
      if (paragraphBuffer.length) {
        const content = paragraphBuffer.join("\n").replace(/\*+/g, "").trim();
        if (content) blocks.push({ type: "p", text: content });
        paragraphBuffer = [];
      }
    };

    lines.forEach((line) => {
      const trimmed = line.trim();
      if (/^###\s/.test(trimmed)) {
        flushParagraph();
        blocks.push({
          type: "h3",
          text: stripMarkdown(trimmed.replace(/^###\s*/, "")),
        });
      } else if (/^##\s/.test(trimmed)) {
        flushParagraph();
        blocks.push({
          type: "h2",
          text: stripMarkdown(trimmed.replace(/^##\s*/, "")),
        });
      } else if (/^#\s/.test(trimmed)) {
        flushParagraph();
        blocks.push({
          type: "h1",
          text: stripMarkdown(trimmed.replace(/^#\s*/, "")),
        });
      } else {
        if (trimmed) paragraphBuffer.push(trimmed.replace(/\*+/g, ""));
        else flushParagraph();
      }
    });
    flushParagraph();
    return blocks;
  };

  const blockClassMap = {
    h1: styles.responseBlockH1,
    h2: styles.responseBlockH2,
    h3: styles.responseBlockH3,
    p: styles.responseBlockP,
  };

  const stageInfo = (Array.isArray(FUNNEL_STAGES)
    ? FUNNEL_STAGES.find((s) => s?.id === activeFunnel)
    : null) ??
    FUNNEL_STAGES?.[0] ?? {
      id: "awareness",
      label: "Awareness",
      icon: "👁",
      desc: "—",
    };
  const thematicAnalysis = safeObject(insights?.thematic_analysis);
  const authorityAnalysis = safeObject(insights?.authority_analysis);
  const topConcerns = safeArray(thematicAnalysis?.top_concerns_raised);
  const topAttributes = safeArray(thematicAnalysis?.top_associated_attributes);
  const topSources = safeArray(authorityAnalysis?.top_influencing_sources);
  const sourceRows = topSources
    .map((s) => {
      if (s == null) return "";
      if (typeof s === "string") return safeString(s);
      // The engine writes { domain, mentions }; older rows may carry name or source.
      const label = safeString(s?.domain ?? s?.name ?? s?.source) || "";
      if (label === "" || label === "Not available") return "";
      const mentions = Number(s?.mentions);
      return Number.isFinite(mentions) && mentions > 0 ? `${label} (${mentions})` : label;
    })
    .filter((label) => label !== "" && label !== "Not available");
  const visibilityDynamics = safeObject(
    insights?.visibility_dynamics?.mention_context_distribution,
  );
  const priorityOpportunities = safeArray(insights?.priority_opportunities);

  const mentionRateVal =
    mentionRate != null && total > 0 ? safeNum(mentionRate) : null;
  const mentionRatePct =
    mentionRateVal != null
      ? `${Math.min(100, Math.max(0, mentionRateVal)).toFixed(2)}%`
      : "Not available";
  const sovLabel =
    sov != null && sov !== "" ? `${safeNum(sov).toFixed(1)}%` : "Not available";
  const avgPosLabel =
    avgPos != null && safeNum(avgPos) > 0
      ? String(safeNum(avgPos))
      : "Not available";

  const mentionFreq =
    competitiveData &&
    typeof competitiveData === "object" &&
    !Array.isArray(competitiveData) &&
    safeKeys(competitiveData).length > 0
      ? competitiveData
      : null;
  const sentimentCounts =
    sentimentDist &&
    typeof sentimentDist === "object" &&
    !Array.isArray(sentimentDist)
      ? sentimentDist
      : null;
  const isSovPercentage =
    mentionFreq &&
    Object.values(mentionFreq).every(
      (v) => safeNum(v) <= 100 && safeNum(v) >= 0,
    );

  const isRTLanguage =
    translationViewLangRef.current === "en"
      ? false
      : isRTL(details?.language ?? recentSimulationDetails?.language ?? "");
  const rtlDir = isRTLanguage ? "rtl" : "ltr";
  const cardTitleWeight = isRTLanguage ? 300 : 600;

  const platformPickerEl = (
    <div className={styles.platformPicker}>
      <div className={styles.platformPickerRow}>
        <span className={styles.platformLabel}>LLM Platform</span>
        <div className={styles.platformOptions}>
          <div
            className={styles.platformThumb}
            style={{
              transform: `translateX(${PLATFORM_OPTIONS.findIndex((p) => p.id === activePlatform) * 100}%)`,
            }}
          />
          {PLATFORM_OPTIONS.map((platform) => {
            const selected = platform.id === activePlatform;
            return (
              <button
                key={platform.id}
                type="button"
                title={modelInfoFor(platform.id)?.title || undefined}
                className={`${styles.platformOption} ${
                  selected ? styles.platformOptionActive : ""
                }`}
                onClick={() => setActivePlatform(platform.id)}
              >
                <span
                  className={styles.platformDot}
                  style={{
                    background: platform.color,
                    opacity: selected ? 1 : 0.45,
                  }}
                />
                <span className={styles.platformName}>{platform.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <span className={styles.platformHint}>
        Showing results for{" "}
        {PLATFORM_OPTIONS.find((p) => p.id === activePlatform)?.label ||
          "ChatGPT"}
        {activeModelInfo?.label && (
          <>
            {" · "}
            <bdi
              dir="ltr"
              className={styles.platformModelId}
              title={activeModelInfo.title}
            >
              {activeModelInfo.label}
            </bdi>
          </>
        )}
      </span>
    </div>
  );

  if (!hasPlatformData) {
    const platformLabel =
      PLATFORM_OPTIONS.find((p) => p.id === activePlatform)?.label ??
      activePlatform;
    return (
      <div className={styles.tabPage}>
        {platformPickerEl}
        <div className={styles.tabHeaderRow}>
          <div>
            <h2 className={styles.personaProfileTitle}>Response Inspector</h2>
          </div>
        </div>
        <Empty
          message={`No ${platformLabel} analysis available yet for this simulation. Results are generated per platform as the simulation completes.`}
        />
      </div>
    );
  }

  return (
    <div className={styles.tabPage} dir={rtlDir}>
      {SHOW_TRANSLATE_BUTTON &&
        translateLanguage &&
        ANALYSIS_DONE_STATUSES.includes(
          Number(details?.status ?? recentSimulationStatus),
        ) &&
        !isSimulationLanguageEnglish(
          details?.language ?? recentSimulationDetails?.language,
        ) && (
          <div
            style={{
              display: "flex",
              justifyContent: isRTLanguage ? "flex-start" : "flex-end",
              marginBottom: 8,
            }}
          >
            <button
              type="button"
              onClick={handleTranslateLanguage}
              disabled={translating}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 16px",
                borderRadius: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "rgba(0,0,0,0.04)",
                color: "#363636",
                fontSize: 13,
                fontWeight: 600,
                cursor: translating ? "not-allowed" : "pointer",
                fontFamily: font,
                opacity: translating ? 0.85 : 1,
              }}
            >
              {translating && (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  aria-hidden
                  style={{ flexShrink: 0 }}
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="rgba(0,0,0,0.12)"
                    strokeWidth="3"
                    fill="none"
                  />
                  <path
                    d="M12 2a10 10 0 0 1 10 10"
                    stroke="#363636"
                    strokeWidth="3"
                    fill="none"
                    strokeLinecap="round"
                  >
                    <animateTransform
                      attributeName="transform"
                      type="rotate"
                      from="0 12 12"
                      to="360 12 12"
                      dur="0.75s"
                      repeatCount="indefinite"
                    />
                  </path>
                </svg>
              )}
              {translating
                ? "Translating…"
                : viewingTranslatedQueries
                  ? "Show original"
                  : "Translate to English"}
            </button>
          </div>
        )}

      {platformPickerEl}

      <div className={styles.tabHeaderRow}>
        <div>
          <h2 className={styles.personaProfileTitle}>Response Inspector</h2>
        </div>
      </div>
      {selectedRun && (
        <div
          onClick={onReturnToLatestRun}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#b45309',
            background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 999, padding: '4px 10px', marginBottom: 12, cursor: 'pointer',
          }}
        >
          ← Viewing Run #{selectedRun.id} — click to return to latest
        </div>
      )}
      <Card className={styles.responseFunnelCard}>
        <span className={styles.responseFunnelIcon}>
          {stageInfo?.icon != null && stageInfo?.icon !== ""
            ? stageInfo.icon
            : "Not available"}
        </span>
        <span className={styles.responseFunnelLabel}>
          Funnel Stage: {na(stageInfo?.label)}
        </span>
        <span className={styles.responseFunnelDesc}>
          — {na(stageInfo?.desc)}
        </span>
      </Card>

      <div className={styles.responseKpiGrid}>
        <KpiCard
          label="Client Mention Rate"
          value={mentionRatePct}
          sub={
            total > 0
              ? `${safeNum(totalQueriesWithMention)} of ${total} queries`
              : "Not available"
          }
          icon="📈"
        />
        <KpiCard
          label="Share of Voice"
          value={sovLabel}
          sub={
            safeString(insights?.brand_name)
              ? `${insights.brand_name} share`
              : "Not available"
          }
          icon="🎯"
        />
        <KpiCard
          label="Avg. Position"
          value={avgPosLabel}
          sub="When mentioned"
          icon="📊"
        />
      </div>

      {perceptionSummary && (
        <Card>
          <CardTitle fontWeight={cardTitleWeight}>Perception Summary</CardTitle>
          <p className={styles.compSummaryText}>{perceptionSummary}</p>
        </Card>
      )}

      {visibilityVerdict && (
        <Card>
          <CardTitle fontWeight={cardTitleWeight}>Visibility Verdict</CardTitle>
          <p className={styles.compSummaryText}>{visibilityVerdict}</p>
        </Card>
      )}

      <div className={styles.responseTwoCol}>
        <Card>
          <CardTitle fontWeight={cardTitleWeight}>
            Brand Mention Frequency
          </CardTitle>
          {mentionFreq && safeKeys(mentionFreq).length > 0 ? (
            Object.entries(mentionFreq)
              .filter(([k]) => k != null && String(k).trim())
              .sort((a, b) => safeNum(b[1]) - safeNum(a[1]))
              .map(([n, c], i) => (
                <div key={`mf-${i}-${n}`} className={styles.responseMentionRow}>
                  <span className={styles.responseMentionRank}>{i + 1}</span>
                  <span className={styles.responseMentionName}>{na(n)}</span>
                  <span className={styles.responseMentionCount}>
                    {c != null && c !== ""
                      ? isSovPercentage
                        ? `${safeNum(c).toFixed(1)}%`
                        : safeNum(c)
                      : "Not available"}
                  </span>
                  <div className={styles.responseMentionBar}>
                    <Bar
                      pct={Math.min(
                        100,
                        Math.max(
                          0,
                          isSovPercentage
                            ? safeNum(c)
                            : (safeNum(c) / Math.max(total, 1)) * 100,
                        ),
                      )}
                      color={safeObject(BRANDS_COLORS)[n] ?? "#6b7280"}
                      showLabel={false}
                      height={5}
                    />
                  </div>
                </div>
              ))
          ) : (
            <p className={styles.responseTabMuted}>Not available</p>
          )}
        </Card>
        <Card>
          <CardTitle fontWeight={cardTitleWeight}>Sentiment</CardTitle>
          {sentimentCounts && safeKeys(sentimentCounts).length > 0 ? (
            Object.entries(sentimentCounts)
              .filter(([_, v]) => safeNum(v) > -1)
              .map(([se, c]) => (
                <div key={`sent-${se}`} className={styles.responseSentimentRow}>
                  <SentimentDot
                    sentiment={typeof se === "string" ? se : "neutral"}
                  />
                  <span className={styles.responseSentimentLabel}>
                    {na(se)}
                  </span>
                  <span className={styles.responseSentimentCount}>
                    {c != null && c !== "" ? safeNum(c) : "Not available"}
                  </span>
                  <div className={styles.responseSentimentBar}>
                    <Bar
                      pct={Math.min(
                        100,
                        Math.max(0, (safeNum(c) / Math.max(total, 1)) * 100),
                      )}
                      color={
                        se === "positive"
                          ? "#22c55e"
                          : se === "negative"
                            ? "#ef4444"
                            : "#f59e0b"
                      }
                      showLabel={false}
                      height={5}
                    />
                  </div>
                </div>
              ))
          ) : (
            <p className={styles.responseTabMuted}>Not available</p>
          )}
        </Card>
      </div>

      {(topObjections.length > 0 || strategicRecommendations.length > 0) && (
        <div className={styles.responseTwoCol}>
          {topObjections.length > 0 && (
            <Card>
              <CardTitle fontWeight={cardTitleWeight}>Top Objections</CardTitle>
              <div className={styles.responseInsightList}>
                {topObjections.map((o, i) => {
                  const label =
                    typeof o === "string"
                      ? o
                      : safeString(o?.text ?? o?.objection ?? o) || "";
                  return (
                    <div
                      key={`obj-${i}`}
                      className={styles.responseInsightItem}
                    >
                      • {na(label)}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
          {strategicRecommendations.length > 0 && (
            <Card>
              <CardTitle fontWeight={cardTitleWeight}>
                Strategic Recommendations
              </CardTitle>
              <div className={styles.responseInsightList}>
                {strategicRecommendations.map((r, i) => {
                  const label =
                    typeof r === "string"
                      ? r
                      : safeString(r?.text ?? r?.recommendation ?? r) || "";
                  return (
                    <div
                      key={`rec-${i}`}
                      className={styles.responseInsightItem}
                    >
                      • {na(label)}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      )}

      {(topConcerns.length > 0 ||
        topAttributes.length > 0 ||
        topSources.length > 0) && (
        <div className={styles.responseInsightsGrid}>
          {topConcerns.length > 0 && (
            <Card>
              <CardTitle
                sub="From extracted insights"
                fontWeight={cardTitleWeight}
              >
                Top Concerns Raised
              </CardTitle>
              <div className={styles.responseInsightList}>
                {topConcerns.map((c, i) => {
                  const label =
                    c != null
                      ? typeof c === "string"
                        ? c
                        : safeString(c?.text ?? c?.concern) || ""
                      : "";
                  return (
                    <div
                      key={`concern-${i}`}
                      className={styles.responseInsightItem}
                    >
                      • {na(label)}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
          {/* {topAttributes.length > 0 && (
            <Card>
              <CardTitle
                sub="From extracted insights"
                fontWeight={cardTitleWeight}
              >
                Top Associated Attributes
              </CardTitle>
              <div className={styles.responseInsightBadges}>
                {topAttributes.map((a, i) => {
                  const label =
                    a != null
                      ? typeof a === "string"
                        ? a
                        : safeString(a?.name ?? a?.attribute) ||
                          (a != null ? String(a) : "")
                      : "";
                  return (
                    <Badge key={`attr-${i}`} color="#6b7280" wrap>
                      {na(label)}
                    </Badge>
                  );
                })}
              </div>
            </Card>
          )} */}
          {topSources.length > 0 && (
            <Card>
              <CardTitle
                sub="From extracted insights"
                fontWeight={cardTitleWeight}
              >
                Top Influencing Sources
              </CardTitle>
              {sourceRows.length === 0 ? (
                <p className={styles.responseTabMuted}>Not available</p>
              ) : (
                <div className={styles.responseInsightList}>
                  {sourceRows.map((label, i) => (
                    <div
                      key={`source-${i}`}
                      className={styles.responseInsightItem}
                    >
                      • {label}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
      )}

      {/* The detailed opportunities card moved here from Competitive Insights. */}
      <Card>
        <CardTitle fontWeight={cardTitleWeight}>
          Priority Opportunities
        </CardTitle>
        {priorityOpportunities.length === 0 ? (
          <p className={styles.compTabMuted}>Not available</p>
        ) : (
          <div className={styles.compOppGrid}>
            {priorityOpportunities.map((o, i) => {
              const title =
                o != null
                  ? typeof o === "string"
                    ? o
                    : safeString(o?.title ?? o?.recommendation) || "—"
                  : "—";
              const desc =
                o != null && typeof o === "object"
                  ? safeString(o?.description ?? o?.detail)
                  : "";
              // One line "Impact: High · Effort: Medium"; a missing value is left out.
              const impact = safeString(o?.impact);
              const effort = safeString(o?.effort);
              const impactEffort = [
                impact && `Impact: ${toCapitalize(impact)}`,
                effort && `Effort: ${toCapitalize(effort)}`,
              ]
                .filter(Boolean)
                .join(" · ");
              const actions = safeArray(o?.action_items ?? o?.actions);
              return (
                <div key={`opp-${i}`} className={styles.compOppCard}>
                  <span className={styles.compOppTitle}>{title || "—"}</span>
                  {impactEffort && (
                    <p className={styles.compOppDesc}>{impactEffort}</p>
                  )}
                  {desc && <p className={styles.compOppDesc}>{desc}</p>}
                  {actions.length > 0 &&
                    actions.map((a, j) => (
                      <div key={j} className={styles.compOppActionRow}>
                        <span className={styles.compOppActionCheck}>✓</span>
                        <span className={styles.compOppActionText}>
                          {a != null ? String(a) : "—"}
                        </span>
                      </div>
                    ))}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {safeKeys(visibilityDynamics).length > 0 && (
        <div className={styles.responseInsightsGrid}>
          {safeKeys(visibilityDynamics).length > 0 && (
            <Card>
              <CardTitle sub="Visibility dynamics" fontWeight={cardTitleWeight}>
                Mention context
              </CardTitle>
              <div className={styles.responseInsightList}>
                {Object.entries(visibilityDynamics).map(([k, v], i) => (
                  <div key={`vd-${i}`} className={styles.responseInsightItem}>
                    • {na(toCapitalize(k))}:{" "}
                    {v != null && v !== "" ? `${safeNum(v)}%` : "Not available"}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      <Card>
        <CardTitle
          sub="Click to expand with brand highlighting"
          fontWeight={cardTitleWeight}
        >
          Sample Responses
        </CardTitle>
        <div className={styles.responseSampleList}>
          {runQueries.length === 0 ? (
            <p className={styles.responseTabMuted}>Not available</p>
          ) : (
            runQueries.map((r, idx) => {
              if (!r || typeof r !== "object") return null;
              const rId = r?.id ?? `q-${idx}`;
              const ex = expandedId === rId;
              const queryText = safeString(r?.query_text) || "";
              const responseText = safeString(r?.response_text) || "";
              return (
                <div
                  key={rId}
                  onClick={() => setExpandedId(ex ? null : rId)}
                  className={`${styles.responseSampleItem} ${ex ? styles.expanded : ""}`}
                >
                  <div className={styles.responseSampleHeader}>
                    <div className={styles.responseSampleHeaderLeft}>
                      <div className={styles.responseSamplePlatformRow}>
                        <div className={styles.responseSampleDot} />
                        <span className={styles.responseSamplePlatform}>
                          {r?.platform === "chatgpt"
                            ? "ChatGPT"
                            : na(r?.platform)}
                        </span>
                      </div>
                      <div className={styles.responseSampleQuery} dir="auto">
                        "{na(queryText)}"
                      </div>
                    </div>
                    <span
                      className={`${styles.responseSampleChevron} ${ex ? styles.expanded : ""}`}
                    >
                      ▾
                    </span>
                  </div>
                  {ex && (
                    <div className={styles.responseSampleBody}>
                      <div className={styles.responseSampleContent}>
                        {formatResponseBlocks(responseText).length === 0 ? (
                          <p className={styles.responseTabMuted}>
                            Not available
                          </p>
                        ) : (
                          formatResponseBlocks(responseText).map(
                            (block, bi) => {
                              if (!block || typeof block !== "object")
                                return null;
                              const blockClass =
                                blockClassMap[block?.type] ??
                                styles.responseBlockP;
                              const firstClass =
                                bi === 0
                                  ? ` ${styles.responseBlockPFirst}`
                                  : "";
                              const isHeading = block?.type !== "p";
                              const blockText = block?.text ?? "";
                              return (
                                <div
                                  key={`block-${bi}`}
                                  className={`${blockClass}${firstClass}`}
                                >
                                  {isHeading ? (
                                    (hl(blockText) || []).map((p, i) =>
                                      p.hl ? (
                                        <span
                                          key={i}
                                          style={{
                                            background: `${BRANDS_COLORS[p.brand] || "#6b7280"}25`,
                                            color:
                                              BRANDS_COLORS[p.brand] ||
                                              "#363636",
                                            padding: "1px 4px",
                                            borderRadius: 3,
                                            fontWeight: 600,
                                            borderBottom: `2px solid ${BRANDS_COLORS[p.brand] || "#6b7280"}`,
                                          }}
                                        >
                                          {p.text}
                                        </span>
                                      ) : (
                                        <span key={i}>{p.text}</span>
                                      ),
                                    )
                                  ) : (
                                    <span className={styles.responseBlockText}>
                                      {(hl(blockText) || []).map((p, i) =>
                                        p.hl ? (
                                          <span
                                            key={i}
                                            style={{
                                              background: `${BRANDS_COLORS[p.brand] || "#6b7280"}25`,
                                              color:
                                                BRANDS_COLORS[p.brand] ||
                                                "#363636",
                                              padding: "1px 4px",
                                              borderRadius: 3,
                                              fontWeight: 600,
                                              borderBottom: `2px solid ${BRANDS_COLORS[p.brand] || "#6b7280"}`,
                                            }}
                                          >
                                            {p.text}
                                          </span>
                                        ) : (
                                          <span key={i}>{p.text}</span>
                                        ),
                                      )}
                                    </span>
                                  )}
                                </div>
                              );
                            },
                          )
                        )}
                      </div>
                      {/* <div className={styles.responseSampleMeta}>
                        <span className={styles.responseSampleMetaItem}>Status: {na(r?.status)}</span>
                      </div> */}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </Card>
      {typeof onGoToCompetitive === "function" && (
        <div className={styles.personaActions}>
          <NextButton
            label="Continue to Competitive Insights"
            onClick={onGoToCompetitive}
            icon={<BarChart2Icon size={16} />}
          />
        </div>
      )}
    </div>
  );
}

export default ResponseTab;
